import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import MainLayout from './components/layout/MainLayout';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Dashboard from './pages/Dashboard';
import TrailerList from './pages/Trailers/TrailerList';
import TrailerDetail from './pages/Trailers/TrailerDetail';
import TrailerCalendar from './pages/Calendar/TrailerCalendar';
import AnalyticsOverview from './pages/Analytics/Overview';
import Organizations from './pages/Admin/Organizations';
import Users from './pages/Admin/Users';
import KoreseddelList from './pages/Koresedler/KoreseddelList';
import RouteList from './pages/Routes/RouteList';
import Settings from './pages/Settings/Settings';
import QuoteCatalogSettings from './pages/Settings/QuoteCatalogSettings';
import WorkHoursPage from './pages/WorkHours/WorkHoursPage';
import CostRuleList from './pages/CostRules/CostRuleList';
import OrgUsersList from './pages/OrgUsers/OrgUsersList';
import SetPassword from './pages/Auth/SetPassword';
import ForgotPassword from './pages/Auth/ForgotPassword';
import PriceCalculatorPage from './pages/PriceCalculator/PriceCalculatorPage';
import QuoteList from './pages/Quotes/QuoteList';
import QuoteDetail from './pages/Quotes/QuoteDetail';
import QuoteForm from './pages/Quotes/QuoteForm';
import QuoteFormNew from './pages/Quotes/QuoteFormNew';
import ProductSalesPage from './pages/ProductSales/ProductSalesPage';
import PriceEnginePage from './pages/PriceEngine/PriceEnginePage';
import EmballagePage from './pages/Emballage/EmballagePage';
import QuotePipeline from './pages/Quotes/QuotePipeline';
import PublicQuoteView from './pages/Quotes/PublicQuoteView';
import PublicPayrollApproval from './pages/PublicPayrollApproval';
import OrderScheduleList from './pages/OrderSchedules/OrderScheduleList';
import OrderScheduleDetail from './pages/OrderSchedules/OrderScheduleDetail';
import OrderScheduleForm from './pages/OrderSchedules/OrderScheduleForm';
import OrderTemplateForm from './pages/OrderSchedules/OrderTemplateForm';
import EconomicPage from './pages/Economic/EconomicPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const theme = createTheme({
  spacing: 8, // Base spacing unit
  palette: {
    primary: {
      main: '#1565c0',
      light: '#1976d2',
      dark: '#0d47a1',
    },
    secondary: {
      main: '#455a64',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
    },
    error: {
      main: '#c62828',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#616161',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '0.875rem',
    },
    body2: {
      fontSize: '0.8125rem',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiButton: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
        sizeSmall: {
          padding: '4px 10px',
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          // Flat olt-tms-style: thin border, no shadow
          boxShadow: 'none',
          border: '1px solid #e5e7eb',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 10,
          '&:last-child': {
            paddingBottom: 10,
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '10px 12px',
        },
        title: {
          fontSize: '0.9375rem',
          fontWeight: 600,
        },
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          fontWeight: 500,
          height: 24,
        },
        sizeSmall: {
          height: 20,
          fontSize: '0.75rem',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        select: {
          // Tighten select padding to match olt-tms density (~32px input height)
          paddingTop: '6px !important',
          paddingBottom: '6px !important',
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem', // 13px
        },
        sizeSmall: {
          fontSize: '0.8125rem',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        // Aggressive density: ~32px tall input instead of MUI default ~40px small
        inputSizeSmall: {
          paddingTop: '6.5px',
          paddingBottom: '6.5px',
          paddingLeft: '10px',
          paddingRight: '10px',
          fontSize: '0.8125rem', // 13px
          lineHeight: 1.4,
          height: 'auto',
        },
        notchedOutline: {
          // Slightly thinner border to feel less heavy
          borderColor: 'rgba(0, 0, 0, 0.18)',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        sizeSmall: {
          fontSize: '0.8125rem',
          // Adjust label position to match smaller input
          transform: 'translate(11px, 8px) scale(1)',
        },
        shrink: {
          fontSize: '0.75rem',
        },
        outlined: {
          '&.MuiInputLabel-shrink': {
            transform: 'translate(11px, -7px) scale(0.85)',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          // Compensate for MUI's extra padding inside Autocomplete
          padding: '2px 10px !important',
          '& .MuiAutocomplete-input': {
            padding: '4px 4px !important',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        sizeSmall: {
          padding: '4px 10px',
          fontSize: '0.75rem',
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          padding: 4,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        sizeSmall: {
          padding: 4,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: '0.8125rem',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 12px',
          fontSize: '0.8125rem',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#f5f5f5',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 6,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          fontSize: '1rem',
          fontWeight: 600,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '8px 16px 12px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: 40,
          padding: '8px 16px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          padding: '6px 12px',
        },
        message: {
          padding: '4px 0',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 4,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        dense: {
          minHeight: 48,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          paddingTop: 6,
          paddingBottom: 6,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
        },
      },
    },
  },
});

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

function MenuGuardRoute({ menuKey, orgFlag, children }: {
  menuKey: string;
  // Feature der er slået til pr. organisation. Gælder også SUPERADMIN: er
  // featuren ikke slået til, findes ruten ikke for den organisation.
  orgFlag?: 'emballageEnabled';
  children: React.ReactNode;
}) {
  const { user, isLoading } = useAuth();

  if (isLoading) return null;

  if (orgFlag && !user?.organization?.[orgFlag]) {
    return <Navigate to="/" replace />;
  }

  // SUPERADMIN always has access
  if (user?.role === 'SUPERADMIN') return <>{children}</>;

  // Empty allowedMenuItems = all visible
  if (!user?.allowedMenuItems?.length) return <>{children}</>;

  // Check if user has access to this menu item
  if (!user.allowedMenuItems.includes(menuKey)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'SUPERADMIN') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { user, isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/set-password"
        element={<SetPassword />}
      />
      <Route
        path="/glemt-adgangskode"
        element={isAuthenticated ? <Navigate to="/" replace /> : <ForgotPassword />}
      />
      <Route
        path="/tilbud/vis/:token"
        element={<PublicQuoteView />}
      />
      <Route
        path="/godkend-loen/:token"
        element={<PublicPayrollApproval />}
      />
      <Route
        path="/change-password"
        element={
          !isAuthenticated ? (
            <Navigate to="/login" replace />
          ) : user?.mustChangePassword ? (
            <ChangePassword />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <MainLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<MenuGuardRoute menuKey="dashboard"><Dashboard /></MenuGuardRoute>} />
        <Route path="trailers" element={<MenuGuardRoute menuKey="trailers"><TrailerList /></MenuGuardRoute>} />
        <Route path="trailers/:id" element={<MenuGuardRoute menuKey="trailers"><TrailerDetail /></MenuGuardRoute>} />
        <Route path="calendar" element={<MenuGuardRoute menuKey="calendar"><TrailerCalendar /></MenuGuardRoute>} />
        <Route path="koresedler" element={<MenuGuardRoute menuKey="koresedler"><KoreseddelList /></MenuGuardRoute>} />
        <Route path="routes" element={<MenuGuardRoute menuKey="routes"><RouteList /></MenuGuardRoute>} />
        <Route path="arbejdstimer" element={<MenuGuardRoute menuKey="arbejdstimer"><WorkHoursPage /></MenuGuardRoute>} />
        <Route path="analytics" element={<MenuGuardRoute menuKey="analytics"><AnalyticsOverview /></MenuGuardRoute>} />
        <Route path="prisberegner" element={<MenuGuardRoute menuKey="prisberegner"><PriceCalculatorPage /></MenuGuardRoute>} />
        <Route path="tilbud" element={<MenuGuardRoute menuKey="tilbud"><QuoteList /></MenuGuardRoute>} />
        <Route path="tilbud/pipeline" element={<MenuGuardRoute menuKey="tilbud"><QuotePipeline /></MenuGuardRoute>} />
        <Route path="tilbud/ny" element={<MenuGuardRoute menuKey="tilbud"><QuoteFormNew /></MenuGuardRoute>} />
        <Route path="tilbud/:id" element={<MenuGuardRoute menuKey="tilbud"><QuoteDetail /></MenuGuardRoute>} />
        <Route path="tilbud/:id/rediger" element={<MenuGuardRoute menuKey="tilbud"><QuoteFormNew /></MenuGuardRoute>} />
        {/* Klassisk redigering bevaret for gamle tilbud (useNewAddressModel=false). Ingen "ny klassisk" rute — kun gamle tilbud kan ramme den. */}
        <Route path="tilbud/:id/rediger-klassisk" element={<MenuGuardRoute menuKey="tilbud"><QuoteForm /></MenuGuardRoute>} />
        <Route path="produktsalg" element={<MenuGuardRoute menuKey="produktsalg"><ProductSalesPage /></MenuGuardRoute>} />
        <Route path="prismotor" element={<MenuGuardRoute menuKey="prismotor"><PriceEnginePage /></MenuGuardRoute>} />
        <Route path="emballage" element={<MenuGuardRoute menuKey="emballage" orgFlag="emballageEnabled"><EmballagePage /></MenuGuardRoute>} />
        <Route path="ordreskemaer" element={<MenuGuardRoute menuKey="ordreskemaer"><OrderScheduleList /></MenuGuardRoute>} />
        <Route path="ordreskemaer/ny" element={<MenuGuardRoute menuKey="ordreskemaer"><OrderScheduleForm /></MenuGuardRoute>} />
        <Route path="ordreskemaer/:id" element={<MenuGuardRoute menuKey="ordreskemaer"><OrderScheduleDetail /></MenuGuardRoute>} />
        <Route path="ordreskemaer/:id/rediger" element={<MenuGuardRoute menuKey="ordreskemaer"><OrderScheduleForm /></MenuGuardRoute>} />
        <Route path="ordreskemaer/:scheduleId/ordrer/ny" element={<MenuGuardRoute menuKey="ordreskemaer"><OrderTemplateForm /></MenuGuardRoute>} />
        <Route path="ordreskemaer/:scheduleId/ordrer/:templateId/rediger" element={<MenuGuardRoute menuKey="ordreskemaer"><OrderTemplateForm /></MenuGuardRoute>} />
        <Route path="economic" element={<MenuGuardRoute menuKey="economic"><EconomicPage /></MenuGuardRoute>} />
        <Route path="omkostninger" element={<MenuGuardRoute menuKey="omkostninger"><CostRuleList /></MenuGuardRoute>} />
        <Route path="settings" element={<MenuGuardRoute menuKey="settings"><Settings /></MenuGuardRoute>} />
        <Route path="settings/quote-catalog" element={<MenuGuardRoute menuKey="settings"><QuoteCatalogSettings /></MenuGuardRoute>} />
        <Route path="brugere" element={<MenuGuardRoute menuKey="brugere"><OrgUsersList /></MenuGuardRoute>} />
        <Route
          path="admin/organizations"
          element={
            <SuperAdminRoute>
              <Organizations />
            </SuperAdminRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <SuperAdminRoute>
              <Users />
            </SuperAdminRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
